import React, { useState, useEffect, useCallback } from "react";
import {
  Container, Header, SpaceBetween, Box, Button, Table,
  Modal, FormField, Input, Alert,
} from "@cloudscape-design/components";
import { UserInfo, SiteConfig } from "../types";
import { apiClient } from "../api/client";

interface Props { user: UserInfo; siteId: string; }

export const SuperAdmin: React.FC<Props> = ({ user, siteId }) => {
  const [sites, setSites] = useState<SiteConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSiteId, setNewSiteId] = useState("");
  const [newSiteName, setNewSiteName] = useState("");
  const [newAdminGroup, setNewAdminGroup] = useState("");

  const fetchSites = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get("/sites");
      setSites(data.sites || []);
    } catch (err) {
      console.error("Failed to fetch sites:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSites(); }, [fetchSites]);

  const handleCreateSite = async () => {
    if (!newSiteId || !newSiteName || !newAdminGroup) return;
    try {
      await apiClient.post("/sites", {
        siteId: newSiteId.toUpperCase(),
        siteName: newSiteName,
        adminGroup: newAdminGroup,
      });
      setShowAddModal(false);
      setNewSiteId("");
      setNewSiteName("");
      setNewAdminGroup("");
      fetchSites();
    } catch (err) {
      console.error("Failed to create site:", err);
    }
  };

  if (!user.isSuperadmin) {
    return <Alert type="error">Access denied. Superadmin privileges required.</Alert>;
  }

  return (
    <SpaceBetween size="l">
      <Container header={
        <Header
          variant="h1"
          description="Manage sites and their admin groups"
          actions={<Button variant="primary" onClick={() => setShowAddModal(true)}>Onboard Site</Button>}
        >
          Super Admin
        </Header>
      }>
        <Table
          loading={loading}
          items={sites}
          columnDefinitions={[
            { id: "siteId", header: "Site ID", cell: (item) => item.siteId, width: 100 },
            { id: "siteName", header: "Site Name", cell: (item) => item.siteName },
            { id: "adminGroup", header: "Admin LDAP Group", cell: (item) => item.adminGroup },
            { id: "callDay", header: "Call Day", cell: (item) => item.callDay || "-", width: 100 },
            { id: "createdAt", header: "Onboarded", cell: (item) => item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-", width: 120 },
          ]}
          empty="No sites configured yet. Click 'Onboard Site' to add the first site."
        />
      </Container>

      <Modal
        visible={showAddModal}
        onDismiss={() => setShowAddModal(false)}
        header="Onboard New Site"
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleCreateSite}>Onboard</Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="m">
          <FormField label="Site ID" description="e.g. CWL1, LHR4, BRS1">
            <Input value={newSiteId} onChange={({ detail }) => setNewSiteId(detail.value)} placeholder="CWL1" />
          </FormField>
          <FormField label="Site Name" description="Full display name">
            <Input value={newSiteName} onChange={({ detail }) => setNewSiteName(detail.value)} placeholder="Swansea - CWL1" />
          </FormField>
          <FormField label="Admin LDAP Group" description="LDAP group for site admin permissions">
            <Input value={newAdminGroup} onChange={({ detail }) => setNewAdminGroup(detail.value)} placeholder="cwl1-rme-planning-admin" />
          </FormField>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
};
